import { CalendarDays, MapPin, Clock, Repeat, ExternalLink, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate, pickLang, fmtTime12 } from "@/lib/format";

/**
 * Full-screen event detail modal.
 *
 * Shows the flyer (or fallback image_url) at full resolution along with all
 * event details. If a registration_url is present, a prominent bilingual
 * "Register / Registrarse" CTA opens the link in a new tab.
 *
 * Props:
 *   ev      Event document (or null/undefined when closed).
 *   onClose () => void  Called when the user dismisses.
 */
export function EventDetailModal({ ev, onClose }) {
  const { t, lang } = useI18n();
  const open = !!ev;
  const title = ev ? pickLang(ev, "title", lang) : "";
  const desc = ev ? pickLang(ev, "description", lang) : "";
  const flyer = ev?.flyer_url || ev?.image_url || "";
  const regUrl = (ev?.registration_url || "").trim();
  const hasReg = !!regUrl && /^https?:\/\//i.test(regUrl);

  const openRegistration = () => {
    if (!hasReg) return;
    // open in new tab/system browser so we don't lose PWA state
    window.open(regUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-2xl w-[96vw] h-[92vh] sm:h-auto sm:max-h-[92vh] p-0 gap-0 rounded-2xl overflow-hidden flex flex-col"
        data-testid="event-detail-modal"
      >
        {ev && (
          <>
            {/* Scrollable body */}
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: "touch" }}
              data-testid="event-detail-scroll"
            >
              {flyer && (
                <div className="relative w-full bg-black/5">
                  <img
                    src={flyer}
                    alt={t.events.flyerAlt}
                    className="block w-full h-auto max-h-[50vh] object-contain"
                    data-testid="event-detail-flyer"
                  />
                </div>
              )}

              <div className="p-5 space-y-4">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-serif-display text-2xl font-semibold leading-tight" data-testid="event-detail-title">
                      {title}
                    </h2>
                    {ev.recurring_weekly && (
                      <Badge variant="secondary" className="gap-1 text-[10px]">
                        <Repeat className="h-3 w-3" />
                        {t.admin?.eventsForm?.recurringBadge || (lang === "es" ? "Cada semana" : "Weekly")}
                      </Badge>
                    )}
                  </div>
                </div>

                <dl className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.events.whenLabel}</dt>
                      <dd className="text-foreground">
                        {fmtDate(ev.event_date, lang)}
                        {ev.event_time && (
                          <span className="inline-flex items-center gap-1 ml-2 text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />{fmtTime12(ev.event_time)}
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                  {ev.location && (
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.events.whereLabel}</dt>
                        <dd className="text-foreground break-words">{ev.location}</dd>
                      </div>
                    </div>
                  )}
                  {desc && (
                    <div>
                      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.events.aboutLabel}</dt>
                      <dd className="mt-1 whitespace-pre-line text-foreground/90 leading-relaxed">
                        {desc}
                      </dd>
                    </div>
                  )}
                </dl>

                {hasReg && (
                  <div className="rounded-xl bg-primary/5 ring-1 ring-primary/15 p-3">
                    <p className="text-xs text-muted-foreground">{t.events.registerHint}</p>
                    <Button
                      onClick={openRegistration}
                      className="mt-2 w-full sm:w-auto"
                      size="lg"
                      data-testid="event-detail-register"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t.events.registerCta}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky footer so the Close button is always reachable, even
                when the event body is long enough to require scrolling. */}
            <div
              className="shrink-0 border-t border-border/60 bg-background p-3 flex justify-end"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
              data-testid="event-detail-footer"
            >
              <Button variant="outline" onClick={onClose} data-testid="event-detail-close">
                <X className="mr-1 h-4 w-4" />
                {t.events.close}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
